<template>
  <div
    class="border overflow-auto border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0">
    <div v-if="uiFlags.isFetchingFlows || uiFlags.isFetchingInBoxes" class="items-center flex text-base justify-center">
      <spinner color-scheme="primary" />
      <p>Loading form</p>
    </div>
    <div v-else class="h-auto  flex flex-col w-[600px]">
      <woot-modal-header :header-title="$t('CAMPAIGN_MANAGER.ADD.TITLE')"
        :header-content="$t('CAMPAIGN_MANAGER.ADD.DESC')" />
      <p class="px-8 pt-2 text-sm text-slate-600 dark:text-slate-300 m-0">
        {{ $t('CAMPAIGN_MANAGER.ADD.STEP_ONE_HINT') }}
      </p>
      <div class="px-8 pt-8">
        <form class="flex flex-col w-full" @submit.prevent="addCampaign">
          <div class="w-full">
            <woot-input v-model="title" :label="$t('CAMPAIGN_MANAGER.ADD.FORM.TITLE.LABEL')" type="text"
              :class="{ error: $v.title.$error }" :error="getTitleError"
              :placeholder="$t('CAMPAIGN_MANAGER.ADD.FORM.TITLE.PLACEHOLDER')" @blur="$v.title.$touch" />

            <label :class="{ error: showInboxValidationError }">
              {{ $t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.LABEL') }}
              <select v-model="inboxID" @change="onInboxChange">
                <option v-for="item in inboxes" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </select>
              <span v-if="showInboxValidationError" class="message">
                {{ inboxValidationMessage }}
              </span>
            </label>
            <p
              v-if="isCheckingInboxAgentBot"
              class="mb-4 mt-2 text-sm text-slate-500 dark:text-slate-300"
            >
              {{ $t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.AGENT_BOT_CHECKING') }}
            </p>
            <div
              v-if="whatsappLimitWarningMessage"
              class="mb-4 mt-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm dark:border-emerald-700/70 dark:bg-emerald-950/35"
            >
              <p class="m-0 font-medium text-sky-900 dark:text-emerald-100">
                {{ $t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.INFO_TITLE') }}
              </p>
              <p class="m-0 mt-1 text-sky-800 dark:text-emerald-50">
                {{ whatsappLimitWarningMessage }}
              </p>
            </div>
            <label :class="{ error: $v.flowID.$error }">
              {{ $t('CAMPAIGN_MANAGER.ADD.FORM.FLOW.LABEL') }}
              <select v-model="flowID">
                <option v-for="item in flows" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </select>
              <span v-if="$v.flowID.$error" class="message">
                {{ $t('CAMPAIGN_MANAGER.ADD.FORM.FLOW.ERROR') }}
              </span>
            </label>

            <div class="mb-4">
              <label class="mb-2 block">
                {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.LABEL') }}
              </label>
              <div class="radio-group">
                <label class="flex items-center gap-2">
                  <input
                    type="radio"
                    name="audienceSource"
                    value="excel"
                    v-model="audienceSource"
                  />
                  <span>{{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SOURCE_EXCEL') }}</span>
                </label>
                <label class="flex items-center gap-2">
                  <input
                    type="radio"
                    name="audienceSource"
                    value="segment"
                    v-model="audienceSource"
                  />
                  <span>{{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SOURCE_SEGMENT') }}</span>
                </label>
              </div>
            </div>

            <div
              v-if="audienceSource === 'excel'"
              class="mb-4"
              :class="{ error: $v.fileID.$error }"
            >

              <div class="drag-and-drop" @dragover.prevent="onDragOver" @drop.prevent="onDrop" @dragleave="onDragLeave"
                @click="openFileDialog" :class="{ 'is-dragover': isDragover }">
                <p v-if="fileName && fileID">Uploaded file: {{ fileName }}</p>
                <p v-else>Drag and drop a file here, or click to select a file</p>

                <input type="file" multiple ref="fileInput" @change="onFileChange" style="display: none;" />
                <input type="hidden" v-model="fileID">
                <div class="upload-progress" v-if="uiFlags.isUploadingFile && fileID == null">
                  <spinner color-scheme="primary" />
                  <p>Uploading.....</p>
                </div>

                <div class="upload-error" v-if="uiFlags.isErrorUpload == true">

                  <p>Error uplopad file....</p>
                  <p>{{ fileErrorUpload  }}</p>
                </div>


              </div>

              <span v-if="$v.fileID.$error" class="message">

                {{ $t('CAMPAIGN_MANAGER.ADD.FORM.FILE.ERROR') }}

              </span>

            </div>

            <div v-else class="mb-4" :class="{ error: $v.segmentId.$error }">
              <label>
                {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SEGMENT_LABEL') }}
                <select v-model="segmentId">
                  <option :value="null" disabled>
                    {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SEGMENT_PLACEHOLDER') }}
                  </option>
                  <option v-for="seg in segments" :key="seg.id" :value="seg.id">
                    {{ seg.name }}
                  </option>
                </select>
                <span v-if="$v.segmentId.$error" class="message">
                  {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SEGMENT_ERROR') }}
                </span>
              </label>

              <div v-if="segmentId" class="mt-3 rounded-md border border-slate-75 dark:border-slate-700/50 bg-slate-25 dark:bg-slate-800 p-4">
                <div class="flex items-center gap-2">
                  <spinner v-if="segmentPreviewLoading" color-scheme="primary" />
                  <p class="m-0 font-medium text-slate-800 dark:text-slate-100">
                    {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SEGMENT_PREVIEW_TITLE') }}
                  </p>
                </div>

                <p v-if="segmentPreviewError" class="mt-2 text-sm text-red-600 dark:text-red-200">
                  {{ segmentPreviewError }}
                </p>

                <template v-else>
                  <p class="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    <b>{{ segmentPreviewCount }}</b>
                    {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SEGMENT_CONTACTS_COUNT_SUFFIX') }}
                  </p>

                  <div class="mt-3">
                    <p class="mb-2 text-sm font-medium text-slate-800 dark:text-slate-100">
                      {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.SEGMENT_FILTERS_TITLE') }}
                    </p>
                    <ul class="m-0 pl-4 text-sm text-slate-700 dark:text-slate-200">
                      <li v-for="(line, idx) in segmentSummaryLines" :key="idx">
                        {{ line }}
                      </li>
                    </ul>
                  </div>
                </template>
              </div>

              <p v-if="!segments.length" class="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {{ $t('CAMPAIGN_MANAGER.ADD.FORM.AUDIENCE.NO_SEGMENTS_HELP') }}
              </p>
            </div>

            <div class="mb-4 flex gap-2 justify-between" v-if="audienceSource === 'excel'">
              <p>
                <a @click="downloadFile">Download Excel Template File</a>
              </p>
              <p>
                <a @click.prevent="openPopup()">File Restrictions</a>
              </p>
            </div>
          </div>

          <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
            <woot-button :disabled="isCheckingInboxAgentBot || !!inboxAgentBotError">
              Next >>
            </woot-button>

          </div>
        </form>
      </div>

      <woot-modal :show.sync="showPopup" :on-close="hidePopup">
        <div class="h-auto overflow-auto flex flex-col">
          <woot-modal-header :header-title="title" :header-content="''" />
          <div class="py-4 px-6 w-full">
            <div>
              <h3>Supported Media Types</h3>
              <h4>Image</h4>
              <table class="table-auto w-full border-collapse border border-gray-200">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="border border-gray-200 px-4 py-2">Image Type</th>
                    <th class="border border-gray-200 px-4 py-2">Extension</th>
                    <th class="border border-gray-200 px-4 py-2">MIME Type</th>
                    <th class="border border-gray-200 px-4 py-2">Max Size</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">JPEG</td>
                    <td class="border border-gray-200 px-4 py-2">.jpeg</td>
                    <td class="border border-gray-200 px-4 py-2">image/jpeg</td>
                    <td class="border border-gray-200 px-4 py-2">5MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">PNG</td>
                    <td class="border border-gray-200 px-4 py-2">.png</td>
                    <td class="border border-gray-200 px-4 py-2">image/png</td>
                    <td class="border border-gray-200 px-4 py-2">5MB</td>
                  </tr>
                </tbody>
              </table>
              <h4>Video</h4>
              <table class="table-auto w-full border-collapse border border-gray-200">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="border border-gray-200 px-4 py-2">Video Type</th>
                    <th class="border border-gray-200 px-4 py-2">Extension</th>
                    <th class="border border-gray-200 px-4 py-2">MIME Type</th>
                    <th class="border border-gray-200 px-4 py-2">Max Size</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">MP4</td>
                    <td class="border border-gray-200 px-4 py-2">.mp4</td>
                    <td class="border border-gray-200 px-4 py-2">video/mp4</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">3GP</td>
                    <td class="border border-gray-200 px-4 py-2">.3gp</td>
                    <td class="border border-gray-200 px-4 py-2">video/3gpp</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                </tbody>
              </table>
              <h4>Audio</h4>
              <table class="table-auto w-full border-collapse border border-gray-200">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="border border-gray-200 px-4 py-2">Audio Type</th>
                    <th class="border border-gray-200 px-4 py-2">Extension</th>
                    <th class="border border-gray-200 px-4 py-2">MIME Type</th>
                    <th class="border border-gray-200 px-4 py-2">Max Size</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">AAC</td>
                    <td class="border border-gray-200 px-4 py-2">.aac</td>
                    <td class="border border-gray-200 px-4 py-2">audio/aac</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">AMR</td>
                    <td class="border border-gray-200 px-4 py-2">.amr</td>
                    <td class="border border-gray-200 px-4 py-2">audio/amr</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">MP3</td>
                    <td class="border border-gray-200 px-4 py-2">.mp3</td>
                    <td class="border border-gray-200 px-4 py-2">audio/mpeg</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">OGG</td>
                    <td class="border border-gray-200 px-4 py-2">.ogg</td>
                    <td class="border border-gray-200 px-4 py-2">audio/ogg</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">OPUS</td>
                    <td class="border border-gray-200 px-4 py-2">.opus</td>
                    <td class="border border-gray-200 px-4 py-2">audio/opus</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">WAV</td>
                    <td class="border border-gray-200 px-4 py-2">.wav</td>
                    <td class="border border-gray-200 px-4 py-2">audio/wav</td>
                    <td class="border border-gray-200 px-4 py-2">16MB</td>
                  </tr>
                </tbody>
              </table>
              <h4>Document</h4>
              <table class="table-auto w-full border-collapse border border-gray-200">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="border border-gray-200 px-4 py-2">Document Type</th>
                    <th class="border border-gray-200 px-4 py-2">Extension</th>
                    <th class="border border-gray-200 px-4 py-2">MIME Type</th>
                    <th class="border border-gray-200 px-4 py-2">Max Size</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border border-gray-200 px-4 py-2">PDF</td>
                    <td class="border border-gray-200 px-4 py-2">.pdf</td>
                    <td class="border border-gray-200 px-4 py-2">application/pdf</td>
                    <td class="border border-gray-200 px-4 py-2">100MB</td>
                  </tr>
                </tbody>
              </table>

            </div>
          </div>
        </div>
      </woot-modal>
    </div>


  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import campaignMixin from 'shared/mixins/campaignMixin';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import ContactAPI from 'dashboard/api/contacts';
import contactFilterItems from 'dashboard/routes/dashboard/contacts/contactFilterItems';
import InboxesAPI from 'dashboard/api/inboxes';


export default {
  components: {
    WootDateTimePicker,
    WootMessageEditor,
    Spinner
  },

  mixins: [alertMixin, campaignMixin],
  data() {
    return {
      showPopup: false,
      show: true,
      enabled: true,
      isDragover: false,
      selectedFile: null,
      segmentPreviewLoading: false,
      segmentPreviewError: '',
      segmentPreviewCount: 0,
      segmentSampleContact: null,
      selectedInboxMeta: null,
      whatsappLimitInfo: null,
      inboxAgentBotId: null,
      inboxAgentBotError: '',
      isCheckingInboxAgentBot: false,
      inboxValidationRequestId: 0,
    };
  },

  validations() {

    const validChars = (value) => /^[a-z0-9-]*$/.test(value)

    const commonValidations = {
      title: {
        required,
        validChars,
      },
      inboxID: {
        required,
        hasAssignedAgentBot() {
          return !!this.inboxAgentBotId;
        },
      },
      flowID: {
        required,
      },

      fileID: {
        required: requiredIf(function requiredFileIfExcel() {
          return this.audienceSource === 'excel';
        }),
      },
      segmentId: {
        required: requiredIf(function requiredSegmentIfSegment() {
          return this.audienceSource === 'segment';
        }),
      },

    };
    /*
        if (this.isOngoingType) {
          return {
            ...commonValidations,
            selectedSender: {
              required,
            },
            endPoint: {
              required,
              shouldBeAValidURLPattern(value) {
                try {
                  // eslint-disable-next-line
                  new URLPattern(value);
                  return true;
                } catch (error) {
                  return false;
                }
              },
              shouldStartWithHTTP(value) {
                if (value) {
                  return (
                    value.startsWith('https://') || value.startsWith('http://')
                  );
                }
                return false;
              },
            },
            timeOnPage: {
              required,
            },
          };
        }
        */
    return {
      ...commonValidations,

    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaignsManager/getUIFlags',
      fileErrorUpload: 'campaignsManager/getFileError',
    }),
    title: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.title
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateTitle', value);
      }
    },

    inboxID: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.inboxID
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateInboxID', value);
      }
    },
    flowID: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.flowID
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateFlowID', value);
      }
    },

    fileID: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.fileID
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateFileID', value);
      }
    },
    fileName: {
      get() {
        let campaing = this.$store.getters['campaignsManager/getCampaign'];
        return campaing.fileName
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateFileName', value);
      }
    },
    audienceSource: {
      get() {
        const campaign = this.$store.getters['campaignsManager/getCampaign'];
        return campaign.audienceSource || 'excel';
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateAudienceSource', value);
      },
    },
    segmentId: {
      get() {
        const campaign = this.$store.getters['campaignsManager/getCampaign'];
        return campaign.segmentId;
      },
      set(value) {
        this.$store.dispatch('campaignsManager/updateSegmentId', value);
      },
    },

    inboxes() {
      return this.$store.getters['campaignsManager/getInboxList'];
    },
    flows() {

      return this.$store.getters['campaignsManager/getFlowList'];
    },
    segments() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        'contact'
      );
    },
    selectedSegment() {
      return this.segments.find(s => s.id === Number(this.segmentId));
    },
    attributeLabelMap() {
      return contactFilterItems.reduce((acc, item) => {
        acc[item.attributeKey] = item.attributeI18nKey;
        return acc;
      }, {});
    },
    segmentSummaryLines() {
      const payload = this.selectedSegment?.query?.payload;
      if (!Array.isArray(payload)) return [];
      return payload.map(filter => this.formatSegmentFilterLine(filter));
    },
    getTitleError() {
      let errorMessage = "";

      errorMessage = this.$v.title.$error && !this.$v.title.required ? this.$t('CAMPAIGN_MANAGER.ADD.FORM.TITLE.ERROR') : ''
      errorMessage = this.$v.title.$error && !this.$v.title.validChars ? this.$t('CAMPAIGN_MANAGER.ADD.FORM.TITLE.ERRORVALIDCHARDS') : ''



      return errorMessage;

    },
    showInboxValidationError() {
      return this.$v.inboxID.$error || !!this.inboxAgentBotError;
    },
    inboxValidationMessage() {
      if (this.$v.inboxID.$error && !this.$v.inboxID.required) {
        return this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.ERROR');
      }

      if (
        this.$v.inboxID.$error &&
        this.$v.inboxID.required &&
        !this.$v.inboxID.hasAssignedAgentBot
      ) {
        return (
          this.inboxAgentBotError ||
          this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.AGENT_BOT_REQUIRED')
        );
      }

      return this.inboxAgentBotError;
    },
    whatsappLimitWarningMessage() {
      if (!this.selectedInboxMeta?.isWhatsApp) return '';
      if (!this.whatsappLimitInfo) return '';

      if (this.whatsappLimitInfo.unlimited_tier) {
        return this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.WHATSAPP_LIMIT_UNLIMITED');
      }

      const remaining = this.whatsappLimitInfo.template_messages_remaining_24h;
      if (remaining === null || remaining === undefined) {
        return this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.WHATSAPP_LIMIT_UNAVAILABLE');
      }

      return this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.WHATSAPP_LIMIT_WARNING', {
        remaining: Number(remaining).toLocaleString(),
      });
    },
  },
  mounted() {

    let params = {};
    this.$store.dispatch('campaignsManager/getFlows', params);
    this.$store.dispatch('campaignsManager/getInboxes', params);
    this.$store.dispatch('campaignsManager/getFileBlob', params);
    this.$store.dispatch('customViews/get', 'contact');
    this.validateSelectedInbox();
    console.log(this.flows)
  },
  methods: {
    ...mapActions('campaignsManager', ['getFileBlob']),
    async downloadFile() {
      await this.getFileBlob();
      let fileBlob = this.$store.getters['campaignsManager/getFileBlobData']
      console.log(fileBlob);

      if (fileBlob) {
        if (fileBlob instanceof Blob) {
          const url = window.URL.createObjectURL(fileBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'template.xlsx'; // Cambia el nombre del archivo según sea necesario
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Liberar el objeto URL
          window.URL.revokeObjectURL(url);
        } else {
          console.error('El objeto recibido no es un Blob válido');
        }
      } else {
        console.log("no hay archivo")
      }
    },
    hidePopup() {
      this.showPopup = false;
      //const currentFlow= this.flowList[this.flowList.length-1]
      //this.$router.push({ name: 'flow_manager_edit', params: { typebotId: currentFlow.typebot_id, flowId:currentFlow.id  } })
    },
    openPopup() {
      this.showPopup = true;
    },
    async addCampaign() {
      this.$v.$touch();

      if (this.isCheckingInboxAgentBot) {
        this.showAlert(
          this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.AGENT_BOT_CHECKING')
        );
        return;
      }

      if (this.$v.$invalid || this.inboxAgentBotError) {
        this.showAlert(this.$t('CAMPAIGN_MANAGER.ADD.FORM.VALIDATION_BEFORE_NEXT'));
        return;
      }
      this.$router.push({
        name: 'campaigns_template'
      });
    },
    async fetchSegmentPreview() {
      if (!this.segmentId) return;
      this.segmentPreviewError = '';
      this.segmentPreviewLoading = true;
      try {
        const { data } = await ContactAPI.getSegmentContacts(this.segmentId, {
          page: 1,
          include_contact_inboxes: false,
          sort: '-last_activity_at',
        });
        this.segmentPreviewCount = data?.meta?.count ?? 0;
        this.segmentSampleContact = Array.isArray(data?.payload)
          ? data.payload[0]
          : null;
      } catch (e) {
        this.segmentPreviewError =
          e?.response?.data?.message ||
          e?.message ||
          'Could not load segment preview';
      } finally {
        this.segmentPreviewLoading = false;
      }
    },
    formatSegmentFilterLine(filter) {
      const key = filter?.attribute_key;
      const i18nKey = this.attributeLabelMap[key];
      const attributeLabel = i18nKey
        ? this.$t(`CONTACTS_FILTER.ATTRIBUTES.${i18nKey}`)
        : key;
      const operator = filter?.filter_operator;
      const operatorLabel = operator
        ? this.$t(`CONTACTS_FILTER.OPERATOR_LABELS.${operator}`)
        : '';
      const values = Array.isArray(filter?.values) ? filter.values : [];
      const valuesText = values.map(v => (v ?? '').toString()).join(', ');
      if (valuesText && operatorLabel) return `${attributeLabel}: ${operatorLabel} ${valuesText}`;
      if (operatorLabel) return `${attributeLabel}: ${operatorLabel}`;
      return `${attributeLabel}`;
    },
    async onInboxChange() {
      this.$v.inboxID.$touch();
      await this.validateSelectedInbox();
    },
    async validateSelectedInbox() {
      this.selectedInboxMeta = null;
      this.whatsappLimitInfo = null;
      this.inboxAgentBotId = null;
      this.inboxAgentBotError = '';
      if (!this.inboxID) return;

      const requestId = ++this.inboxValidationRequestId;
      this.isCheckingInboxAgentBot = true;

      try {
        const [inboxResponse, agentBotResponse] = await Promise.all([
          InboxesAPI.show(this.inboxID),
          InboxesAPI.getAgentBot(this.inboxID),
        ]);

        if (requestId !== this.inboxValidationRequestId) return;

        const inbox = inboxResponse?.data || {};
        const agentBot = agentBotResponse?.data?.agent_bot || {};

        const isWhatsApp = inbox.channel_type === 'Channel::Whatsapp';
        this.inboxAgentBotId = agentBot?.id || null;
        this.inboxAgentBotError = this.inboxAgentBotId
          ? ''
          : this.$t('CAMPAIGN_MANAGER.ADD.FORM.INBOX.AGENT_BOT_REQUIRED');

        this.selectedInboxMeta = { isWhatsApp };
        if (!isWhatsApp) {
          this.isCheckingInboxAgentBot = false;
          return;
        }
      } catch (error) {
        if (requestId !== this.inboxValidationRequestId) return;
        this.selectedInboxMeta = { isWhatsApp: false };
        this.whatsappLimitInfo = null;
        this.inboxAgentBotId = null;
        this.inboxAgentBotError = this.$t(
          'CAMPAIGN_MANAGER.ADD.FORM.INBOX.AGENT_BOT_CHECK_FAILED'
        );
        return;
      }

      try {
        const refreshResponse = await InboxesAPI.refreshWhatsAppQualityLimits(this.inboxID);
        if (requestId !== this.inboxValidationRequestId) return;
        this.whatsappLimitInfo = refreshResponse?.data?.payload || {};
      } catch (error) {
        if (requestId !== this.inboxValidationRequestId) return;
        this.whatsappLimitInfo = { template_messages_remaining_24h: null };
      } finally {
        if (requestId === this.inboxValidationRequestId) {
          this.isCheckingInboxAgentBot = false;
        }
      }
    },
    onDragOver(event) {
      this.isDragover = true;
    },
    onDragLeave(event) {
      this.isDragover = false;
    },
    onDrop(event) {
      this.isDragover = false;
      const files = event.dataTransfer.files;
      this.handleFiles(files);
    },
    onFileChange(event) {
      const files = this.$refs.fileInput.files;
      this.handleFiles(files);
    },
    handleFiles(files) {
      if (files.length > 0) {

        this.selectedFile = files[0];
        this.$store.dispatch('campaignsManager/uploadFile', this.selectedFile);

      } else {
        this.selectedFile = null;
      }
    },
    openFileDialog() {

      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    }
  },
  watch: {
    audienceSource(newVal) {
      if (newVal === 'segment') {
        // clear excel data
        this.$store.dispatch('campaignsManager/updateFileID', null);
        this.$store.dispatch('campaignsManager/updateFileName', '');
      } else if (newVal === 'excel') {
        // clear segment data
        this.$store.dispatch('campaignsManager/updateSegmentId', null);
        this.segmentPreviewCount = 0;
        this.segmentSampleContact = null;
        this.segmentPreviewError = '';
      }
    },
    segmentId() {
      this.fetchSegmentPreview();
    },
  },

};
</script>
<style lang="scss" scoped>
::v-deep .ProseMirror-woot-style {
  height: 5rem;
}

.message-editor {
  @apply px-3;

  ::v-deep {
    .ProseMirror-menubar {
      @apply rounded-tl-[4px];
    }
  }
}

h3, h4, table {
  margin-bottom: 1rem; /* Add this line to add bottom margin */
}

.drag-and-drop {
  border: 2px dashed #ccc;
  border-radius: 5px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
}

.drag-and-drop.is-dragover {
  border-color: #333;
}

.upload-error p {
  color: red;
}
</style>
